import React, { useState } from 'react';
import { Check, Users, Bell, MessageSquare, Share2, FileText, UserCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useOverlayState } from '../context/OverlayContext';
import ShareModal from './ShareModal';

interface VybeCreatedModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventTitle: string;
  eventId: string;
}

const VybeCreatedModal: React.FC<VybeCreatedModalProps> = ({
  isOpen,
  onClose,
  eventTitle,
  eventId,
}) => {
  const navigate = useNavigate();
  const [showShareModal, setShowShareModal] = useState(false);
  useOverlayState(isOpen, 'vybe-created-modal');

  if (!isOpen) return null;

  const handleViewVybe = () => {
    console.log('[VybeCreatedModal] Navigating to vybe:', eventId);
    if (!eventId) {
      console.error('[VybeCreatedModal] No eventId provided!');
      return;
    }
    onClose();
    // Navigate after a small delay to ensure modal closes first
    setTimeout(() => {
      navigate(`/vybes/${eventId}`);
    }, 100);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleViewMyVybes = () => {
    onClose();
    setTimeout(() => {
      navigate('/myvybes');
    }, 100);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      navigate('/vybes');
    }, 100);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        className="bg-theme-cards rounded-2xl shadow-card w-full max-w-md border border-theme-cards overflow-hidden"
      >
        <div className="bg-gradient-to-br from-red-500 to-pink-600 p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Check size={40} className="text-theme-primary" />
          </motion.div>
          <h2 className="text-2xl font-sora font-bold text-white mb-2">
            Vybe Created! 🎉
          </h2>
          <p className="text-white/90 text-sm">
            <strong>{eventTitle}</strong> is now live and ready for requests
          </p>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-theme-background/50 border border-white/10 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center">
              <span className="mr-2">📋</span>
              What Happens Next
            </h3>
            <ul className="space-y-3 text-sm text-white/80">
              <li className="flex items-start">
                <Bell className="w-5 h-5 mr-3 mt-0.5 text-theme-primary flex-shrink-0" />
                <span>You'll get notified when people request to join</span>
              </li>
              <li className="flex items-start">
                <UserCheck className="w-5 h-5 mr-3 mt-0.5 text-theme-primary flex-shrink-0" />
                <span>Head to your Vybe page to approve or pass on requests as they come in</span>
              </li>
              <li className="flex items-start">
                <MessageSquare className="w-5 h-5 mr-3 mt-0.5 text-theme-primary flex-shrink-0" />
                <span>Once people are accepted, chat with everyone in the group thread</span>
              </li>
              <li className="flex items-start">
                <FileText className="w-5 h-5 mr-3 mt-0.5 text-theme-primary flex-shrink-0" />
                <span>Use the chat to coordinate exact meeting details like "meet at the fountain at 3pm"</span>
              </li>
              <li className="flex items-start">
                <Share2 className="w-5 h-5 mr-3 mt-0.5 text-theme-primary flex-shrink-0" />
                <span>Share your Vybe with your crew to make it even better</span>
              </li>
            </ul>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleViewVybe}
              className="flex-1 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 py-3 rounded-full font-semibold transition-all shadow-lg"
            >
              View Vybe
            </button>
            <button
              onClick={handleShare}
              className="flex items-center justify-center gap-2 px-6 py-3 border border-white/20 rounded-full text-white hover:bg-white/10 transition-colors font-semibold"
            >
              <Share2 size={18} />
              Share
            </button>
          </div>

          <button
            onClick={handleViewMyVybes}
            className="w-full py-3 text-white/70 hover:text-white transition-colors font-medium text-sm"
          >
            Go to My Vybes
          </button>

          <button
            onClick={handleClose}
            className="w-full py-3 text-white/70 hover:text-white transition-colors font-medium text-sm"
          >
            Back to Explore
          </button>
        </div>
      </motion.div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title={eventTitle}
        shareUrl={`${window.location.origin}/vybes/${eventId}`}
      />
    </div>
  );
};

export default VybeCreatedModal;
